"use client";

import { StreamController } from "@/lib/whiteboard/streaming/controller";
import { StreamingRenderer } from "@chickytutor/whiteboard-stream-renderer/react";
import { type ReactNode, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  LessonButton,
  LessonFeedback,
  LessonHeading,
  LessonInput,
  LessonPresentationProvider,
  useLessonPresentation,
} from "../LessonPrimitives";
import { ReadingStoryCard } from "../ReadingStoryCard";
import { WhiteboardCard } from "../WhiteboardCard";
import { WhiteboardHeadingContext } from "../WhiteboardHeadingContext";
import { InstantWhiteboardContext } from "../WhiteboardWriteInText";
import styles from "./StreamingBoard.module.css";
import { useStreamingScroll } from "./useStreamingScroll";

const control = "stream-control min-h-11 rounded-lg border px-3 py-2 text-base disabled:opacity-60";
function ActivityControl({ controller, id }: { controller: StreamController; id: string }) {
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );
  const activity = snapshot.activities[id];
  const [hint, setHint] = useState(false);
  if (!activity || !snapshot.ready.includes(id)) return null;
  const answer = snapshot.answers[id] ?? "";
  const result = snapshot.results[id];
  const locked = result === "correct";
  return (
    <section className="stream-activity space-y-3" aria-label="Practice">
      <p className="stream-prompt font-medium">{activity.prompt}</p>
      {activity.mode === "choice" ? (
        <div className="flex flex-wrap gap-2">
          {activity.options.map((option) => (
            <LessonButton
              key={option}
              type="button"
              aria-pressed={answer === option}
              disabled={locked}
              className={control}
              onClick={() => controller.answer(id, option)}
            >
              {option}
            </LessonButton>
          ))}
        </div>
      ) : (
        <LessonInput
          aria-label={activity.prompt}
          maxLength={2000}
          value={answer}
          disabled={locked}
          aria-invalid={result === "incorrect"}
          className={`${control} w-full`}
          onChange={(event) => controller.answer(id, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) controller.check(id);
          }}
        />
      )}
      <div className="flex flex-wrap gap-2">
        <LessonButton
          intent="primary"
          type="button"
          className={`${control} stream-check font-semibold`}
          disabled={locked || !answer.trim()}
          onClick={() => controller.check(id)}
        >
          {locked ? "Correct" : "Check"}
        </LessonButton>
        {activity.hint && (
          <LessonButton
            type="button"
            className={control}
            onClick={() => setHint(!hint)}
            aria-expanded={hint}
          >
            Hint
          </LessonButton>
        )}
      </div>
      {hint && (
        <LessonFeedback tone="hint" className="text-sm">
          {activity.hint}
        </LessonFeedback>
      )}
      <LessonFeedback
        as="div"
        tone={result === "incorrect" ? "incorrect" : result === "correct" ? "correct" : "muted"}
        role="status"
        className="text-sm"
        data-result={result}
      >
        {result === "correct" ? (
          <p>Nice work. {activity.explanation}</p>
        ) : result === "incorrect" ? (
          <p>Try once more. You can open the hint.</p>
        ) : null}
      </LessonFeedback>
    </section>
  );
}

export function StreamingBoard({
  controller,
  onDisplayed,
  variant = "dark",
  embedded = false,
  headerExtra,
}: {
  controller: StreamController;
  onDisplayed?: () => void;
  variant?: "photo" | "dark";
  embedded?: boolean;
  headerExtra?: ReactNode;
}) {
  const container = useRef<HTMLDivElement>(null);
  const lesson = useLessonPresentation();
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );
  const scroll = useStreamingScroll(container, controller);
  // Content below the fold is not yet seen; only acknowledge once it enters view.
  // Scrolling away later is not reported as hiding (that means minimized).
  const inViewport = useRef(typeof IntersectionObserver === "undefined");
  const acknowledgeRef = useRef<() => void>(() => {});
  useEffect(() => {
    const node = container.current;
    if (typeof IntersectionObserver === "undefined" || !node) return;
    const observer = new IntersectionObserver(([entry]) => {
      inViewport.current = entry?.isIntersecting ?? false;
      if (inViewport.current) acknowledgeRef.current();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const acknowledge = () => {
      if (!container.current?.getClientRects().length || document.visibilityState === "hidden") {
        controller.hidden();
        return;
      }
      if (!inViewport.current) return;
      controller.mounted();
      onDisplayed?.();
    };
    acknowledgeRef.current = acknowledge;
    acknowledge();
    const observer = new ResizeObserver(acknowledge);
    if (container.current) observer.observe(container.current);
    document.addEventListener("visibilitychange", acknowledge);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", acknowledge);
    };
  }, [
    controller,
    snapshot.spec,
    snapshot.exercises,
    snapshot.phase,
    snapshot.reviewing,
    snapshot.withdrawn,
    onDisplayed,
  ]);
  useEffect(() => () => controller.hidden(), [controller]);
  const partial = snapshot.phase === "preparing";
  const usable = snapshot.ready.filter(
    (id) => !snapshot.reviewing.includes(id) && !snapshot.withdrawn.includes(id)
  );
  return (
    <div
      ref={container}
      className={styles.board}
      data-variant={variant}
      data-embedded={embedded}
      data-has-header={Boolean(headerExtra)}
      data-host-scroll={scroll.hostScroll}
      data-stream-board
    >
      {headerExtra && <div className={styles.header}>{headerExtra}</div>}
      <LessonPresentationProvider>
        <div className={styles.content}>
          <StreamingRenderer
            heading={
              lesson
                ? (text) => <LessonHeading className="stream-heading">{text}</LessonHeading>
                : undefined
            }
            spec={snapshot.spec}
            reveal={(label, text, open) => controller.disclose(label, text, open)}
            revealOpen={(label, text) =>
              Object.entries(snapshot.spec.elements).some(
                ([id, item]) =>
                  item.type === "Reveal" &&
                  item.props.label === label &&
                  item.props.text === text &&
                  snapshot.revealed[id]
              )
            }
            activity={(id) => <ActivityControl key={id} id={id} controller={controller} />}
            exercise={(id) => {
              const unit = snapshot.exercises[id];
              if (!unit) return null;
              const reviewing = snapshot.reviewing.includes(id);
              // The teaching review is invisible to the learner. A pending task
              // holds its exact space, hidden, so nothing moves when it appears in
              // its checked form; a task the review withdrew never appears.
              if (snapshot.withdrawn.includes(id)) return null;
              return (
                <InstantWhiteboardContext.Provider
                  // A repaired task mounts fresh when it becomes visible.
                  key={`${id}:${reviewing ? "pending" : "shown"}`}
                  value={true}
                >
                  <div
                    className={reviewing ? `stream-native ${styles.pendingTask}` : "stream-native"}
                    data-exercise-kind={unit.spec.kind}
                    data-native-result={snapshot.results[id]}
                    data-review={reviewing ? "pending" : undefined}
                    aria-hidden={reviewing || undefined}
                    inert={reviewing || undefined}
                  >
                    {unit.readingEnd && unit.spec.kind === "reading_story" ? (
                      <ReadingStoryCard
                        spec={unit.spec}
                        showContent={false}
                        onEvent={(event) => controller.nativeEvent(id, event)}
                      />
                    ) : (
                      <WhiteboardHeadingContext.Provider
                        value={{ showTitle: false, showSectionLabels: true }}
                      >
                        <WhiteboardCard
                          spec={unit.spec}
                          variant={variant}
                          embedded
                          renderState={
                            // The choice the tutor is naming pulses with the tutor highlight style.
                            snapshot.emphasis?.id === id
                              ? {
                                  ...unit.renderState,
                                  highlightTargets: [
                                    ...unit.renderState.highlightTargets,
                                    snapshot.emphasis.target,
                                  ],
                                }
                              : unit.renderState
                          }
                          onEvent={(event) => controller.nativeEvent(id, event)}
                        />
                      </WhiteboardHeadingContext.Provider>
                    )}
                  </div>
                </InstantWhiteboardContext.Provider>
              );
            }}
          />
        </div>
      </LessonPresentationProvider>
      <LessonFeedback role="status" className="stream-muted mt-4 text-sm" tone={"muted"}>
        {partial
          ? usable.length
            ? "You can try this while Chicky prepares the rest."
            : snapshot.reviewing.length
              ? ""
              : "Chicky is preparing your practice…"
          : snapshot.phase === "failed"
            ? usable.length
              ? "The rest couldn’t load. You can still use the practice shown here."
              : "This practice couldn’t finish loading. Please try again."
            : snapshot.phase === "cancelled"
              ? usable.length
                ? "Preparation stopped. Your practice stays here."
                : "Preparation stopped."
              : ""}
      </LessonFeedback>
    </div>
  );
}
